
import React from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardIcon } from "@heroicons/react/24/outline"; 
import { FiChevronRight } from "react-icons/fi";

const CodeBlock = ({ code }) => {
  const [copied, setCopied] = React.useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="relative bg-black/90 rounded-lg overflow-hidden mb-4">
      <div className="absolute top-2 right-2 z-10">
        <button
          onClick={copyToClipboard}
          className="p-1.5 rounded text-white"
          title="Copy to clipboard"
        >
          {copied ? (
            <span className="text-green-400 text-lg leading-none">✔</span>
          ) : (
            <ClipboardIcon className="w-4 h-4" />
          )}
        </button>
      </div>

      <pre className="text-xs text-green-400 p-4 pt-8 font-mono overflow-x-auto">
        {code}
      </pre>
    </div>
  );
};

export function renderWindowsMonitoringGuide() {
  return <WindowsMonitoringComponent />;
}

function WindowsMonitoringComponent() {
  const navigate = useNavigate();
    const colorMap = {
  blue: "bg-blue-100 dark:bg-blue-900/30",
  green: "bg-green-100 dark:bg-green-900/30",
  purple: "bg-purple-100 dark:bg-purple-900/30",
  orange: "bg-orange-100 dark:bg-orange-900/30",
  indigo: "bg-indigo-100 dark:bg-indigo-900/30",
  pink: "bg-pink-100 dark:bg-pink-900/30",
  cyan: "bg-cyan-100 dark:bg-cyan-900/30",
  yellow: "bg-yellow-100 dark:bg-yellow-900/30",
};
    return (
      <div>
        <main className="transition-opacity duration-300">
          <div className="py-3">
    
            {/* Breadcrumbs */}
            <nav className="flex flex-wrap items-center gap-2 text-sm text-[#9a734c] mb-8">
          <a className="hover:text-primary hover:underline" href="#">Netraa APM</a>
          <FiChevronRight />
          <span>Agent Setup Guide</span>
          <FiChevronRight />
          <span>Infra</span>
          <FiChevronRight />
          <span className="text-[#1b140d] dark:text-white font-medium">Windows</span>
        </nav>
    
            {/* Intro */}
            <div id="windows-intro" className="mb-10 scroll-mt-24">
              <span className="inline-block px-3 py-1 mb-4 text-xs font-bold tracking-wider text-orange-600 uppercase bg-orange-100 rounded-full dark:bg-orange-900/30 dark:text-orange-400">
                Documentation
              </span>
    
              <h1 className="text-3xl lg:text-4xl font-extrabold text-text-main dark:text-white tracking-tight mb-4">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-red-600">
                  Windows Monitoring Setup Guide
                </span>
              </h1>
              <p className="text-lg text-text-muted dark:text-gray-400">
                This guide explains how to set up Windows host monitoring using:
              </p>
    
              <ul className="list-disc ml-6 text-text-muted mt-3 space-y-1">
                <li>windows_exporter (Prometheus metrics)</li>
                <li>OpenTelemetry Collector (otelcol)</li>
                <li>Custom Java Metrics Exporter (MetricsExporter-0.0.1-SNAPSHOT.jar)</li>
                <li>Exporting data to the AOP Server</li>
              </ul>
            </div>
    
            {/* Prerequisites */}
            {/* <section id="windows-prerequisites" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl font-bold mb-4">Prerequisites</h2>
              <ul className="list-disc ml-6 text-text-muted space-y-1">
                <li>Java 17+ installed</li>
                <li>JAVA_HOME set (optional but recommended)</li>
              </ul>
            </section> */}
    
            <h2 className="text-2xl font-bold mb-4 scroll-mt-24" id="steps">Steps to follow:-</h2>
    
    {[
      {
        step: "1",
        color: "blue",
        title: "Extract Infra Agent Package",
        content: (
          <>
            <p>1. Download infra_agent_win.zip from the provided apmosys share point.</p>
            <p>2. Extract the contents to a directory, for example:</p>
            <CodeBlock code={`C:\\infra_agent_win`} />
    
            <p>The extracted folder should contain:</p>
            <ul className="list-disc ml-6">
              <li>windows_exporter-*.msi</li>
              <li>otelcol-*.msi</li>
              <li>config.yaml</li>
              <li>MetricsExporter-0.0.1-SNAPSHOT.jar</li>
            </ul>
          </>
        )
      },
      {
        step: "2",
        color: "green",
        title: "Install Windows Exporter",
        content: (
          <>
            <p>1. Run the installer:</p>
            <CodeBlock code={`windows_exporter-*.msi`} />
    
            <p>2. The installer will automatically:</p>
            <ul className="list-disc ml-6">
              <li>Install windows_exporter</li>
              <li>Register it as a Windows service</li>
              <li>Start the service</li>
            </ul>
          </>
        )
      },
      {
        step: "3",
        color: "purple",
        title: "Verify Windows Exporter is Running",
        content: (
          <>
            <p>Option A: Using Services UI</p>
            <p>1. Press Win + R → type:</p>
            <CodeBlock code={`services.msc`} />
    
            <p>2. Look for:</p>
            <p>windows_exporter</p>
    
            <p>3. Ensure the service status is Running.</p>
    
            <p>Option B: Using Browser</p>
            <p>Open a browser and visit:</p>
            <CodeBlock code={`http://localhost:9182/metrics`} />
          </>
        )
      },
      {
        step: "4",
        color: "orange",
        title: "Install OpenTelemetry Collector (otelcol)",
        content: (
          <>
            <p>1. Run the installer:</p>
            <CodeBlock code={`otelcol-*.msi`} />
    
            <p>2. The installer will automatically:</p>
            <ul className="list-disc ml-6">
              <li>Install OpenTelemetry Collector</li>
              <li>Register it as a Windows service</li>
              <li>Start the service</li>
            </ul>
          </>
        )
      },
      {
        step: "5",
        color: "indigo",
        title: "Update OpenTelemetry Collector Configuration",
        content: (
          <>
            <p>1. Navigate to:</p>
            <CodeBlock code={`C:\\Program Files\\OpenTelemetry Collector`} />
    
            <p>2. Delete existing config file:</p>
            <CodeBlock code={`config.yaml (or otel-config.yaml)`} />
    
            <p>3. Copy new config:</p>
            <CodeBlock code={`C:\\infra_agent_win\\Otelcol\\config.yaml`} />
          </>
        )
      },
      {
        step: "6",
        color: "pink",
        title: "Restart OpenTelemetry Collector Service",
        content: (
          <>
            <p>1. Open:</p>
            <CodeBlock code={`services.msc`} />
    
            <p>2. Locate:</p>
            <p>OpenTelemetry Collector</p>
    
            <p>3. Stop → Start service</p>
            <p>This reloads the updated configuration.</p>
          </>
        )
      },
      {
        step: "7",
        color: "yellow",
        title: "Validation Checklist",
        content: (
          <>
            <ul className="list-disc ml-6 space-y-1">
              <li>windows_exporter → http://localhost:9182/metrics → OK</li>
              <li>Metrics Exporter → Running</li>
              <li>otelcol → Running</li>
              <li>AOP Server → Metrics visible</li>
            </ul>
          </>
        )
      }
    ].map((item, idx) => (
      <section key={idx} className="mb-12">
    
        <h2 className="flex items-center gap-3 text-2xl font-bold mb-6">
    
          <span className={`w-10 h-10 flex items-center justify-center rounded-lg ${colorMap[item.color]}`}>
            {item.step}
          </span>
    
          {item.title}
    
        </h2>
    
        <div className="text-sm text-text-muted space-y-2 whitespace-pre-line">
          {item.content}
        </div>
    
      </section>
    ))}
          {/* Visualize Data CTA */}
                    <section className="mb-10 flex justify-center">
                      <button
                        onClick={() => navigate("/infrahost")}
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold text-white
                                   bg-gradient-to-r from-orange-500 to-red-600
                                   hover:from-orange-600 hover:to-red-700
                                   shadow-md hover:shadow-lg transition-all duration-300
                                   dark:shadow-orange-900/30"
                      >
                        Visualize Data
                        <FiChevronRight className="w-4 h-4" />
                      </button>
                    </section>
          </div>
        </main>
      </div>
    );
    }